import React, { useEffect, useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Toolbar,
  Stack,
  Typography,
  Divider,
  Button,
  Breadcrumbs,
  Link,
  Box,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';

import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useParams, useNavigate } from 'react-router';
import { useUser } from '@/providers';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SyncIcon from '@mui/icons-material/Sync';

import ErrorSnackBar from '@/components/shared/ErrorSnackBar';

import toNiceTimestamp from '@/util/toNiceTimestamp';
import {
  deleteProject,
  getProject,
  refreshProjectGpuTypes,
} from '@/actions/projects';
import { refreshProjectDatastores } from '@/actions/projectDatastores';
import { refreshProjectDataConnectors } from '@/actions/projectDataConnectors';
import { refreshProjectServices } from '@/actions/projectServices';
import CredentialsForm from '@/components/credentials/CredentialsForm';
import ProjectMembers from './ProjectMembers';
import LoadingMask from '@/components/shared/LoadingMask';
import ProjectDatastores from './ProjectDatastores';
import ProjectDataConnectors from './ProjectDataConnectors';
import ProjectServices from './ProjectServices';
import ProjectSecrets from './ProjectSecrets';
import ProjectAuthorizers from './ProjectAuthorizers';

export default function Project() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();

  const api_error = useSelector((state) => state.errorMessage);
  const projects = useSelector((state) => state.projects);
  const user = useUser();

  const [project, setProject] = useState(
    projects ? projects.find((project) => project.id === id) : undefined,
  );
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [actionsMenuAnchor, setActionsMenuAnchor] = useState(null);

  const handleActionsMenuOpen = (event) => {
    setActionsMenuAnchor(event.currentTarget);
  };

  const handleActionsMenuClose = () => {
    setActionsMenuAnchor(null);
  };

  const handleRefreshResourceTypes = async () => {
    setTransitioning(true);
    handleActionsMenuClose();
    await dispatch(refreshProjectGpuTypes(id));
    setTransitioning(false);
  };

  const handleRefreshProjectDatastores = async () => {
    setTransitioning(true);
    handleActionsMenuClose();
    await dispatch(refreshProjectDatastores(id));
    setTransitioning(false);
  };

  const handleRefreshProjectDataConnectors = async () => {
    setTransitioning(true);
    handleActionsMenuClose();
    await dispatch(refreshProjectDataConnectors(id));
    setTransitioning(false);
  };

  const handleRefreshProjectServices = async () => {
    setTransitioning(true);
    handleActionsMenuClose();
    await dispatch(refreshProjectServices(id));
    setTransitioning(false);
  };

  useEffect(() => {
    (async () => {
      if (!project) {
        const project_data = await getProject(id);
        if (!project_data) {
          navigate(`/projects`);
        }
        setProject(project_data);
      }
      setLoading(false);
    })();
  }, [dispatch, navigate, project, id]);

  if (loading) {
    return <LoadingMask />;
  }

  return (
    <Container>
          <Breadcrumbs aria-label='breadcrumb'>
            <Link
              underline='hover'
              color='inherit'
              component={RouterLink}
              to={`/dashboard`}
            >
              Home
            </Link>
            <Link
              underline='hover'
              color='inherit'
              component={RouterLink}
              to={`/projects`}
            >
              Projects
            </Link>
            <Typography color='text.primary'>{project.name}</Typography>
          </Breadcrumbs>
          <br />
          <Paper sx={{ display: 'flex', width: '100%' }}>
            <Grid container spacing={4} sx={{ flexGrow: 1 }}>
              <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                <Paper sx={{ display: 'flex', width: '100%' }}>
                  <Toolbar>
                    <Stack spacing={2} direction='row' alignItems='center'>
                      <Typography variant='h5'>{project.name}</Typography>
                      <Button
                        onClick={() =>
                          navigate(`/project/edit?id=${id}`)
                        }
                        disabled={transitioning}
                      >
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <EditIcon />
                          <Typography variant='button' display='block'>
                            Edit
                          </Typography>
                        </Stack>
                      </Button>
                      <Button
                        onClick={() => {
                          if (user) {
                            dispatch(deleteProject(id, user));
                          }
                          navigate(`/projects`);
                        }}
                        disabled={transitioning}
                      >
                        <Stack direction='row' spacing={1} alignItems='center'>
                          <DeleteIcon />
                          <Typography variant='button' display='block'>
                            Delete
                          </Typography>
                        </Stack>
                      </Button>
                      <Button
                        onClick={handleActionsMenuOpen}
                        disabled={transitioning}
                        endIcon={<MoreVertIcon />}
                      >
                        Actions
                      </Button>
                    </Stack>
                  </Toolbar>
                </Paper>
                <Box padding='15px'>
                  <Typography variant='h6'>Basic Information</Typography>
                  <Divider />
                  <br />
                  <Grid
                    container
                    component='dl' // mount a Definition List
                    spacing={0}
                    sx={{ display: 'table' }}
                  >
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '200px' }}
                      >
                        <b>ID:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {project.id}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '200px' }}
                      >
                        <b>Created:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {toNiceTimestamp(project.createdAt, true)}
                      </Typography>
                    </Grid>
                    <Grid size="grow">
                      <Typography
                        component='dt'
                        variant='body1'
                        sx={{ display: 'table-cell', width: '200px' }}
                      >
                        <b>Owned Resources Only:</b>
                      </Typography>
                      <Typography
                        component='dd'
                        variant='body2'
                        sx={{ display: 'table-cell' }}
                      >
                        {project.owned_resources_only ? 'True' : 'False'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
                <Divider />
                <br />

                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <ProjectMembers key={`members-${id}`} project={project} />
                </Grid>
                <Divider />
                <br />
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <CredentialsForm
                    key={`credentials-${id}`}
                    project_uuid={id}
                  />
                </Grid>
                <Divider />
                <br />
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <ProjectSecrets key={`secrets-${id}`} project={project} />
                </Grid>
                <Divider />
                <br />
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <ProjectAuthorizers
                    key={`authorizers-${id}`}
                    project={project}
                  />
                </Grid>
                <Divider />
                <br />
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <ProjectDatastores
                    key={`datastores-${id}`}
                    project={project}
                  />
                </Grid>
                <Divider />
                <br />
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <ProjectDataConnectors
                    key={`data-connectors-${id}`}
                    project={project}
                  />
                </Grid>
                <Divider />
                <br />
                <Grid size={{ xs: 12, md: 12, lg: 12 }}>
                  <ProjectServices key={`services-${id}`} project={project} />
                </Grid>
              </Grid>
            </Grid>
          </Paper>
          <Menu
            anchorEl={actionsMenuAnchor}
            open={Boolean(actionsMenuAnchor)}
            onClose={handleActionsMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <MenuItem
              onClick={handleRefreshResourceTypes}
              disabled={transitioning}
            >
              <ListItemIcon>
                <SyncIcon />
              </ListItemIcon>
              <ListItemText>Refresh Resource Types</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleRefreshProjectDatastores}
              disabled={transitioning}
            >
              <ListItemIcon>
                <SyncIcon />
              </ListItemIcon>
              <ListItemText>Refresh Project Datastores</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleRefreshProjectDataConnectors}
              disabled={transitioning}
            >
              <ListItemIcon>
                <SyncIcon />
              </ListItemIcon>
              <ListItemText>Refresh Project Data Connectors</ListItemText>
            </MenuItem>
            <MenuItem
              onClick={handleRefreshProjectServices}
              disabled={transitioning}
            >
              <ListItemIcon>
                <SyncIcon />
              </ListItemIcon>
              <ListItemText>Refresh Project Services</ListItemText>
            </MenuItem>
          </Menu>
          <ErrorSnackBar message={api_error} />
        </Container>
  );
}
